<template>
  <div class="mainfont page-container">
    <v-snackbar v-model="showSnackBar" color="black">
      <v-layout wrap justify-center>
        <v-flex text-left class="align-self-center">
          <span style="color: white">
            {{ msg }}
          </span>
        </v-flex>
        <v-flex text-right>
          <v-btn small :ripple="false" text @click="showSnackBar = false">
            <v-icon style="color: white">mdi-close</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-snackbar>
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#13736f"
      spinner="spinner"
    />

    <!-- Hero -->
    <section class="nc-hero">
      <img class="nc-hero-img" src="../../assets/Images/kerala_forest_hero_1.jpg" alt="" />
      <div class="nc-hero-shade"></div>
      <div class="nc-hero-content">
        <span class="nc-hero-eyebrow">
          <v-icon small color="#bbf7d0">mdi-pine-tree</v-icon>
          Kerala Forest Department
        </span>
        <h1 class="nc-hero-title">Find your nature camp</h1>
        <p class="nc-hero-text">
          Stay in the forest, learn from naturalists and explore Kerala's wild places. Pick a camp and apply in a few minutes.
        </p>
        <div class="nc-hero-stats">
          <span class="nc-hero-stat">
            <strong>{{ heroCount }}</strong> camp{{ heroCount === 1 ? "" : "s" }} available
          </span>
          <span v-if="typeCounts.free !== undefined" class="nc-hero-stat">
            <strong>{{ typeCounts.free }}</strong> free
          </span>
          <span v-if="typeCounts.paid !== undefined" class="nc-hero-stat">
            <strong>{{ typeCounts.paid }}</strong> paid
          </span>
          <span class="nc-hero-stat"><strong>{{ district.length }}</strong> districts</span>
        </div>
      </div>
    </section>

    <!-- Camp type tabs + filters -->
    <section class="nc-filters">
      <div class="nc-type-tabs-row">
        <div class="nc-type-tabs" role="tablist" aria-label="Camp type">
          <button
            v-for="t in typeTabs"
            :key="t.key"
            type="button"
            role="tab"
            class="nc-type-tab"
            :class="['nc-type-tab--' + t.key, { 'nc-type-tab--active': campType === t.key }]"
            :aria-selected="campType === t.key ? 'true' : 'false'"
            @click="campType = t.key"
          >
            <v-icon small>{{ t.icon }}</v-icon>
            <span>{{ t.label }}</span>
            <span v-if="typeCounts[t.key] !== undefined" class="nc-type-count">{{ typeCounts[t.key] }}</span>
          </button>
        </div>

        <router-link
          to="/how-to-apply?from=naturecamps"
          class="nc-how-btn"
          title="View document requirements, 100 KB file limits & step-by-step instructions"
        >
          <v-icon small color="#13736f" class="mr-1">mdi-file-document-check-outline</v-icon>
          <span>How to Apply</span>
          <v-icon small color="#13736f" class="nc-how-arrow ml-1">mdi-arrow-right</v-icon>
        </router-link>
      </div>

      <div class="nc-filter-row">
        <v-text-field
          v-model="search"
          placeholder="Search by camp name, district or description"
          prepend-inner-icon="mdi-magnify"
          clearable
          outlined
          dense
          hide-details
          color="#13736f"
          background-color="white"
          class="nc-field nc-field--search"
        ></v-text-field>
        <v-btn
          v-if="$vuetify.breakpoint.xsOnly"
          depressed
          class="btn-act btn-act--neutral nc-filter-toggle"
          @click="showFilters = !showFilters"
        >
          <v-icon small left>mdi-tune-variant</v-icon>
          Filters
          <span v-if="activeFilters.length" class="nc-filter-badge">{{ activeFilters.length }}</span>
        </v-btn>
      </div>

      <div v-show="showFilters || !$vuetify.breakpoint.xsOnly" class="nc-filter-grid">
        <v-autocomplete
          v-model="districtid"
          :items="district"
          label="District"
          prepend-inner-icon="mdi-map-marker-outline"
          clearable
          outlined
          dense
          hide-details
          color="#13736f"
          background-color="white"
          class="nc-field"
        ></v-autocomplete>
        <v-autocomplete
          v-model="divisionid"
          :items="divisions"
          item-text="officeName"
          item-value="_id"
          label="Forest division"
          prepend-inner-icon="mdi-domain"
          no-data-text="No divisions"
          clearable
          outlined
          dense
          hide-details
          color="#13736f"
          background-color="white"
          class="nc-field"
        ></v-autocomplete>
        <v-select
          v-model="minMembers"
          :items="groupSizes"
          label="Group size"
          prepend-inner-icon="mdi-account-group-outline"
          clearable
          outlined
          dense
          hide-details
          color="#13736f"
          background-color="white"
          class="nc-field"
        ></v-select>
        <v-select
          v-model="sort"
          :items="sortOptions"
          label="Sort by"
          prepend-inner-icon="mdi-sort"
          outlined
          dense
          hide-details
          color="#13736f"
          background-color="white"
          class="nc-field"
        ></v-select>
      </div>
    </section>

    <!-- Results line -->
    <div v-if="!appLoading" class="nc-results">
      <span class="nc-results-count">
        <strong>{{ totalData }}</strong>
        {{ campType === "all" ? "" : campType }} camp{{ totalData === 1 ? "" : "s" }}
        {{ activeFilters.length ? "match your filters" : "available" }}
      </span>
      <span v-for="f in activeFilters" :key="f.key" class="nc-active-chip">
        {{ f.label }}
        <button type="button" class="nc-active-chip-x" :aria-label="'Remove ' + f.label" @click="removeFilter(f.key)">
          <v-icon x-small>mdi-close</v-icon>
        </button>
      </span>
      <button v-if="activeFilters.length || campType !== 'all'" type="button" class="nc-clear" @click="resetFilters">
        <v-icon x-small color="#13736f">mdi-filter-remove-outline</v-icon>
        Clear all
      </button>
    </div>

    <!-- Camps card grid -->
    <div v-if="list.length > 0" class="nc-grid">
      <article v-for="(item, i) in list" :key="item._id || i" class="nc-card">
        <div class="nc-media">
          <safe-image class="nc-img" :src="campImage(item)" :fallback="campFallbackImage(item, i)" :alt="item.campName" loading="lazy" />
          <div class="nc-media-shade"></div>
          <span class="nc-type" :class="item.campType === 'free' ? 'nc-type--free' : 'nc-type--paid'">
            <v-icon x-small :color="item.campType === 'free' ? '#15803d' : '#b45309'">
              {{ item.campType === "free" ? "mdi-gift-outline" : "mdi-currency-inr" }}
            </v-icon>
            {{ item.campType === "free" ? "Free camp" : "Paid camp" }}
          </span>
          <span v-if="item.district" class="nc-district">
            <v-icon x-small color="white">mdi-map-marker</v-icon>
            {{ item.district }}
          </span>
        </div>

        <div class="nc-body">
          <h3 class="nc-title" :title="item.campName">{{ item.campName }}</h3>
          <p v-if="plainText(item.caption)" class="nc-caption">{{ plainText(item.caption) }}</p>

          <div class="nc-facts">
            <span class="nc-fact">
              <v-icon small color="#13736f">mdi-account-group-outline</v-icon>
              Max Participants {{ item.totalMembers }}
            </span>
            <span v-if="item.divisionid && item.divisionid.officeName" class="nc-fact">
              <v-icon small color="#13736f">mdi-domain</v-icon>
              {{ item.divisionid.officeName }}
            </span>
            <span class="nc-fact" :class="item.campType === 'free' ? 'nc-fact--free' : ''">
              <v-icon small :color="item.campType === 'free' ? '#15803d' : '#b45309'">mdi-ticket-outline</v-icon>
              {{ item.campType === "free" ? "No fee" : "Fee applies" }}
            </span>
          </div>

          <v-btn
            block
            depressed
            class="btn-act btn-act--primary nc-apply"
            :loading="checkingDraftsFor === item._id"
            @click="applyNow(item)"
          >
            Apply now
            <v-icon right small class="nc-apply-icon">mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </article>
    </div>

    <section v-else-if="!appLoading" class="surface">
      <div class="empty-state">
        <v-icon size="56">mdi-forest</v-icon>
        <span class="nc-empty-title">No camps match your filters</span>
        <span>Try another district, camp type or group size.</span>
        <v-btn
          v-if="activeFilters.length || campType !== 'all'"
          depressed
          class="btn-act btn-act--primary mt-2"
          @click="resetFilters"
        >
          <v-icon small left>mdi-filter-remove-outline</v-icon>
          Clear filters
        </v-btn>
      </div>
    </section>

    <div v-if="pages > 1" class="nc-pagination">
      <v-pagination
        v-model="currentPage"
        :length="pages"
        :total-visible="$vuetify.breakpoint.xsOnly ? 5 : 7"
        prev-icon="mdi-chevron-left"
        next-icon="mdi-chevron-right"
        color="#13736f"
      ></v-pagination>
    </div>

    <v-dialog
      :retain-focus="true"
      persistent
      v-model="dialog2"
      max-width="350px"
      :fullscreen="$vuetify.breakpoint.xsOnly"
    >
      <v-card class="mainfont">
        <div class="dialog-head">
          <div>
            <div class="dialog-head-title">Add accommodation</div>
            <div class="dialog-head-subtitle">Enter the accommodation details.</div>
          </div>
          <v-btn icon dark @click="dialog2 = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="pt-5">
          <v-form @submit.prevent="validateInput">
            <v-text-field
              label="Accommodation name"
              color="#13736f"
              outlined
              background-color="white"
              dense
              v-model="accName"
              hide-details
              class="mb-4"
            ></v-text-field>
          </v-form>
          <v-form @submit.prevent="validateInput">
            <v-text-field
              label="No of rooms"
              color="#13736f"
              outlined
              background-color="white"
              dense
              type="number"
              v-model="noOfRooms"
              hide-details
              class="mb-4"
            >
            </v-text-field>
          </v-form>
          <v-form @submit.prevent="validateInput">
            <v-text-field
              label="Max guests per room"
              color="#13736f"
              outlined
              background-color="white"
              dense
              type="number"
              v-model="maxGuestPerRoom"
              hide-details
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn text class="text-none font-weight-bold" @click="dialog2 = false">Cancel</v-btn>
          <v-btn depressed color="#13736f" class="white--text text-none font-weight-bold px-6" @click="addIb()">
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      :retain-focus="true"
      persistent
      v-model="editdailog"
      max-width="350px"
      :fullscreen="$vuetify.breakpoint.xsOnly"
    >
      <v-card class="mainfont">
        <div class="dialog-head">
          <div>
            <div class="dialog-head-title">Edit accommodation</div>
            <div class="dialog-head-subtitle">Update the accommodation details.</div>
          </div>
          <v-btn icon dark @click="editdailog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-text class="pt-5">
          <v-form @submit.prevent="validateInput">
            <v-text-field
              label="Accommodation name"
              color="#13736f"
              outlined
              background-color="white"
              dense
              type="text"
              v-model="curid.accName"
              hide-details
              class="mb-4"
            ></v-text-field>
          </v-form>
          <v-form @submit.prevent="validateInput">
            <v-text-field
              label="Number of rooms"
              color="#13736f"
              outlined
              background-color="white"
              dense
              type="number"
              v-model="curid.noOfRooms"
              hide-details
              class="mb-4"
            ></v-text-field>
          </v-form>
          <v-form @submit.prevent="validateInput">
            <v-text-field
              label="Max guests per room"
              color="#13736f"
              outlined
              background-color="white"
              dense
              type="number"
              v-model="curid.maxGuestPerRoom"
              hide-details
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn text class="text-none font-weight-bold" @click="editdailog = false">Cancel</v-btn>
          <v-btn depressed color="#13736f" class="white--text text-none font-weight-bold px-6" @click="edit()">
            Edit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      :retain-focus="true"
      persistent
      v-model="deletedialog"
      max-width="600px"
    >
      <v-card class="mainfont">
        <div class="dialog-head dialog-head--danger">
          <div>
            <div class="dialog-head-title">Delete IB</div>
            <div class="dialog-head-subtitle">This action cannot be undone.</div>
          </div>
        </div>
        <v-card-text class="pt-5 nc-dialog-text">
          Are you sure you want to delete this IB?
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-spacer></v-spacer>
          <v-btn text class="text-none font-weight-bold" @click="deletedialog = false">Cancel</v-btn>
          <v-btn depressed color="#dc2626" class="white--text text-none font-weight-bold px-6" @click="deleteItem()">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reminder shown on "Apply now" when the user already has saved drafts -->
    <v-dialog v-model="draftNotice" max-width="440px" @click:outside="closeDraftNotice">
      <v-card class="mainfont">
        <div class="dialog-head">
          <div>
            <div class="dialog-head-title">You have draft applications</div>
            <div class="dialog-head-subtitle">Just a reminder before you start a new one.</div>
          </div>
        </div>
        <v-card-text class="pt-5 nc-dialog-text">
          You have <strong>{{ draftCount }}</strong> saved draft
          application{{ draftCount === 1 ? "" : "s" }} that
          {{ draftCount === 1 ? "has" : "have" }} not been submitted yet.
          <ul v-if="draftCamps.length" class="nc-draft-list">
            <li v-for="(name, i) in draftCamps" :key="i">{{ name }}</li>
          </ul>
          <div v-if="draftCount > draftCamps.length" class="nc-draft-more">
            and {{ draftCount - draftCamps.length }} more
          </div>
          You can continue them anytime from <strong>Draft Applications</strong>.
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-btn text class="text-none font-weight-bold" color="#13736f" @click="viewDrafts">
            View drafts
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn depressed color="#13736f" class="white--text text-none font-weight-bold px-6" @click="proceedToApply">
            OK, got it
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Booking calendar shown before the apply form: pick up to 3 days -->
    <v-dialog v-model="calendarDialog" persistent max-width="460px">
      <v-card class="mainfont">
        <div class="dialog-head">
          <div>
            <div class="dialog-head-title">Select your dates</div>
            <div class="dialog-head-subtitle">{{ pendingCamp ? pendingCamp.campName : "" }}</div>
          </div>
        </div>
        <v-card-text class="pt-4 px-3">
          <div class="d-flex justify-center">
            <v-progress-circular v-if="loadingDates" indeterminate color="#13736f" class="my-12" />
            <v-date-picker
              v-else
              v-model="pickedDates"
              range
              no-title
              full-width
              color="#13736f"
              :min="pickerMinDate"
              :max="latestDate"
              :allowed-dates="isSelectable"
              :events="dateEvents"
              :event-color="dateEventColor"
              class="nc-cal"
            ></v-date-picker>
          </div>
          <div class="nc-legend">
            <span><i class="nc-dot" style="background:#fecaca;border-color:#dc2626"></i>Already booked</span>
            <span><i class="nc-dot" style="background:#e2e8f0;border-color:#94a3b8"></i>Not available</span>
            <span><i class="nc-dot" style="background:#13736f;border-color:#13736f"></i>Today</span>
          </div>
          <div class="nc-cal-note">
            <v-icon x-small color="#13736f" class="mr-1">mdi-information-outline</v-icon>
            Choose a start and end date (maximum 3 days).
          </div>
          <div class="nc-policy">
            <v-icon small color="#b45309" class="mr-2">mdi-calendar-clock</v-icon>
            <div>
              <div class="nc-policy-title">Advance Booking Policy (7 Days Gap)</div>
              <div class="nc-policy-text">
                Bookings must be made at least 7 days in advance. Dates within the next 7 days are disabled.
              </div>
            </div>
          </div>
          <div v-if="rangeError" class="nc-cal-note" style="color:#dc2626">{{ rangeError }}</div>
          <div v-if="rangeSummary" class="nc-cal-summary">{{ rangeSummary }}</div>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-btn text class="text-none font-weight-bold" @click="closeCalendar">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            depressed
            color="#13736f"
            class="white--text text-none font-weight-bold px-6"
            :disabled="!selectedStart"
            @click="confirmDates"
          >
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
  <script>
import axios from "axios";
import { campPhoto } from "@/utils/campPhotos";
import SafeImage from "@/components/Common/SafeImage";

export default {
  components: { SafeImage },
  data() {
    return {
      name: null,
      barlist: [],
      userProfileTabs: null,
      subtab: null,
      dist: this.$route.query.district,
      appLoading: false,
      page: 1,
      pages: 0,
      dialog1: false,

      preview: null,
      tab: null,
      barlist1: [],
      dialog3: false,
      deletedialog: false,
      editdailog: false,
      draftNotice: false,
      draftCount: 0,
      draftCamps: [],
      pendingCamp: null,
      checkingDraftsFor: null,
      calendarDialog: false,
      loadingDates: false,
      pickedDates: [],
      rangeError: "",
      bookedDates: [],
      heldDates: [],
      blockedDates: [],

      minAmount: null,
      password: null,
      categoryarray: [],
      list: [],
      flag: false,
      flag2: false,
      auctionbased: [],
      menu1: false,
      menu2: false,
      menu3: false,
      menu4: false,
      limit: 10,

      fromDate: null,
      toDate: null,
      fromDate2: null,
      toDate2: null,
      noOfRooms: null,
      range: [],
      district: [
        "Thiruvananthapuram",
        "Kollam",
        "Pathanamthitta",
        "Alappuzha",
        "Kottayam",
        "Idukki",
        "Ernakulam",
        "Thrissur",
        "Palakkad",
        "Malappuram",
        "Kozhikode",
        "Wayanad",
        "Kannur",
        "Kasaragod",
      ],
      division: [],
      accName: null,
      divisionid: null,
      circleid: null,
      rangeid: null,
      districtid: null,
      maxGuestPerRoom: null,

      circle: [],
      msg: null,
      currentPage: 1,
      totalData: 0,
      // Cards per row in the grid; the page size is a whole number of rows
      gridCols: 4,

      // Filters (sent to /all/naturecamp)
      campType: "all",
      search: "",
      minMembers: null,
      sort: "recommended",
      showFilters: false,
      typeCounts: {},
      divisions: [],
      typeTabs: [
        { key: "all", label: "All camps", icon: "mdi-view-grid-outline" },
        { key: "paid", label: "Paid", icon: "mdi-currency-inr" },
        { key: "free", label: "Free", icon: "mdi-gift-outline" },
      ],
      groupSizes: [
        { text: "10 or more people", value: 10 },
        { text: "20 or more people", value: 20 },
        { text: "30 or more people", value: 30 },
        { text: "40 or more people", value: 40 },
        { text: "50 or more people", value: 50 },
      ],
      sortOptions: [
        { text: "Recommended (District order)", value: "recommended" },
        { text: "Name (A to Z)", value: "name" },
        { text: "Largest groups first", value: "capacityHigh" },
        { text: "Smallest groups first", value: "capacityLow" },
        { text: "Newest first", value: "newest" },
      ],

      showSnackBar: false,
      curid: [],
      dialog2: false,
    };
  },
  watch: {
    // A range longer than 3 days, or one covering a taken date, isn't accepted:
    // the date just clicked becomes the new start date instead.
    pickedDates(val, old) {
      // A third click next to an existing range extends it (1 Nov - 2 Nov, then
      // 3 Nov gives 1 Nov - 3 Nov) when the result is still valid.
      if (val.length === 1 && old && old.length === 2) {
        const [oa, ob] = [...old].sort();
        const c = val[0];
        const lo = c < oa ? c : oa;
        const hi = c > ob ? c : ob;
        if (c < oa || c > ob) {
          const problem = this.rangeProblem(lo, hi);
          if (problem === "") {
            this.pickedDates = [lo, hi];
          } else {
            // Can't extend: the clicked date becomes the new start, and we say why
            this.rangeError = problem;
          }
          return;
        }
      }
      if (val.length !== 2) {
        this.rangeError = "";
        return;
      }
      const [a, b] = [...val].sort();
      const bad = this.rangeProblem(a, b);
      if (bad) {
        const clicked = val.find((d) => !(old || []).includes(d)) || b;
        this.pickedDates = [clicked];
        this.$nextTick(() => {
          this.rangeError = bad;
        });
      } else {
        this.rangeError = "";
      }
    },
    rangeid() {
      this.currentPage = 1;
      this.getList();
    },
    districtid() {
      this.onDistrictChange();
    },
    campType() {
      this.onDistrictChange();
    },
    divisionid() {
      this.onDistrictChange();
    },
    minMembers() {
      this.onDistrictChange();
    },
    sort() {
      this.onDistrictChange();
    },
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.onDistrictChange(), 400);
    },
    currentPage() {
      this.getList();
    },
    // Window resized so the grid has a different number of columns: refill pages from the start
    pageLimit(val) {
      if (val !== this.requestedLimit) this.onDistrictChange();
    },
  },

  computed: {
    pickerMinDate() {
      return this.ymd(new Date());
    },
    earliestDate() {
      return this.addDays(this.ymd(new Date()), 7);
    },
    latestDate() {
      return this.addDays(this.ymd(new Date()), 75);
    },
    selectedStart() {
      return this.pickedDates.length ? [...this.pickedDates].sort()[0] : null;
    },
    selectedDays() {
      if (!this.pickedDates.length) return 0;
      const [a, b] = [...this.pickedDates].sort();
      return b ? Math.round((new Date(b) - new Date(a)) / 86400000) + 1 : 1;
    },
    rangeSummary() {
      if (!this.selectedStart) return "";
      const [a, b] = [...this.pickedDates].sort();
      const f = (v) => new Date(v + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
      return b && b !== a ? `${f(a)} to ${f(b)} (${this.selectedDays} days)` : `${f(a)} (1 day)`;
    },
    // About 15 cards per page, rounded up to full rows so no row is left with empty slots
    pageLimit() {
      const cols = Math.max(1, this.gridCols);
      return Math.ceil(15 / cols) * cols;
    },
    heroCount() {
      return this.typeCounts.all !== undefined ? this.typeCounts.all : this.totalData;
    },
    // Chips shown under the filter bar.
    activeFilters() {
      const out = [];
      if (this.search && this.search.trim()) out.push({ key: "search", label: '"' + this.search.trim() + '"' });
      if (this.districtid) out.push({ key: "districtid", label: this.districtid });
      if (this.divisionid) {
        const div = this.divisions.find((d) => d._id === this.divisionid);
        out.push({ key: "divisionid", label: div ? div.officeName : "Division" });
      }
      if (this.minMembers) out.push({ key: "minMembers", label: this.minMembers + "+ people" });
      return out;
    },
  },

  beforeDestroy() {
    clearTimeout(this.searchTimer);
    clearTimeout(this.resizeTimer);
    window.removeEventListener("resize", this.onResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },

  mounted() {
    this.updateGridCols();
    window.addEventListener("resize", this.onResize);
    // On a hard refresh the layout (sidebar, scrollbar, fonts) settles after mount, so the width
    // measured above can be wrong. Watching the container itself re-measures once it settles.
    const bar = this.$el && this.$el.querySelector && this.$el.querySelector(".nc-filters");
    if (bar && typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(bar);
    }
    this.getList();
    // this.getCircle();
  },

  methods: {
    // getDivision() {
    //   this.appLoading = true;
    //   const selectedCircle = this.circle.find((c) => c._id === this.circleid);
    //   if (selectedCircle) {
    //     const path = selectedCircle.path;
    //     axios({
    //       method: "post",
    //       url: "/get/divsions",
    //       headers: {
    //         token: localStorage.getItem("token"),
    //       },
    //       data: {
    //         path: path,
    //       },
    //     })
    //       .then((response) => {
    //         this.division = response.data.data;
    //         this.appLoading = false;
    //       })
    //       .catch((err) => {
    //         this.ServerError = true;
    //         console.log(err);
    //       });
    //   }
    // },
    // Returns custom camp image if uploaded, otherwise deterministic static photo
    campImage(item) {
      return campPhoto(item, item && item.image);
    },
    campFallbackImage(item, i) {
      return campPhoto(item ? (item._id || i) : i);
    },
    // Mirrors .nc-grid: repeat(auto-fill, minmax(270px, 1fr)) with a 20px gap (1 column on phones)
    updateGridCols() {
      // The filter bar is always rendered and is as wide as the grid
      const bar = this.$el && this.$el.querySelector && this.$el.querySelector(".nc-filters");
      const width = bar && bar.clientWidth;
      if (!width) return;
      this.gridCols = this.$vuetify.breakpoint.xsOnly ? 1 : Math.max(1, Math.floor((width + 20) / (270 + 20)));
    },
    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(this.updateGridCols, 200);
    },
    // Back to page 1 and reload (the currentPage watcher reloads when the page changes).
    onDistrictChange() {
      if (this.currentPage !== 1) {
        this.currentPage = 1;
      } else {
        this.getList();
      }
    },
    removeFilter(key) {
      this[key] = key === "search" ? "" : null;
    },
    resetFilters() {
      this.search = "";
      this.districtid = null;
      this.divisionid = null;
      this.minMembers = null;
      this.campType = "all";
      this.sort = "recommended";
    },
    // Captions are saved from a rich-text editor; show them as plain text.
    plainText(html) {
      if (!html) return "";
      return String(html)
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, " ")
        .trim();
    },
    getDistrictData() {
      this.onDistrictChange();
    },
    // Remind the user about unsubmitted drafts before opening the apply form.
    // Only a reminder: a failed check never blocks applying.
    applyNow(item) {
      this.pendingCamp = item;
      this.checkingDraftsFor = item._id;
      axios({
        method: "POST",
        url: "/user/applications",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: {
          page: 1,
          limit: 3,
          stage: "draft",
        },
      })
        .then((response) => {
          const drafts = response.data.data || [];
          this.draftCount = response.data.totalLength || drafts.length;
          if (this.draftCount > 0) {
            this.draftCamps = drafts.map((d) => (d.campid && d.campid.campName) || "Nature camp");
            this.draftNotice = true;
          } else {
            this.proceedToApply();
          }
        })
        .catch((err) => {
          console.error(err);
          this.proceedToApply();
        })
        .finally(() => {
          this.checkingDraftsFor = null;
        });
    },
    // Clicked outside the reminder: dismiss it without starting an application.
    closeDraftNotice() {
      this.draftNotice = false;
      this.pendingCamp = null;
    },
    // Draft reminder acknowledged (or no drafts): show the booking calendar.
    proceedToApply() {
      this.draftNotice = false;
      if (!this.pendingCamp) return;
      this.pickedDates = [];
      this.rangeError = "";
      this.bookedDates = [];
      this.heldDates = [];
      this.blockedDates = [];
      this.calendarDialog = true;
      this.loadingDates = true;
      axios({
        method: "POST",
        url: "/camp/booked/dates",
        headers: { token: localStorage.getItem("token") },
        data: { campid: this.pendingCamp._id },
      })
        .then((response) => {
          if (response.data.status) {
            this.bookedDates = response.data.booked || [];
            this.heldDates = response.data.held || [];
            this.blockedDates = response.data.blocked || [];
          }
        })
        .catch((err) => console.error(err))
        .finally(() => {
          this.loadingDates = false;
        });
    },
    closeCalendar() {
      this.calendarDialog = false;
      this.pendingCamp = null;
    },
    confirmDates() {
      const item = this.pendingCamp;
      if (!item || !this.selectedStart) return;
      const query = {
        id: item._id,
        district: item.district,
        startDate: this.selectedStart,
        campDuration: String(this.selectedDays),
      };
      this.calendarDialog = false;
      this.pendingCamp = null;
      this.$router.push({ path: "/booknow", query });
    },
    ymd(d) {
      const p = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    },
    addDays(val, n) {
      const d = new Date(val + "T00:00:00");
      d.setDate(d.getDate() + n);
      return this.ymd(d);
    },
    isUnavailable(val) {
      return (
        this.bookedDates.includes(val) ||
        this.heldDates.includes(val) ||
        this.blockedDates.includes(val)
      );
    },
    // Past dates, the 7 day gap and taken dates can't be picked. Everything else is
    // free to pick; the 3 day limit is checked once an end date is chosen.
    isSelectable(val) {
      if (val < this.earliestDate || val > this.latestDate) return false;
      if (this.isUnavailable(val)) return false;
      return true;
    },
    // "" when a..b is a valid selection, otherwise the reason it isn't.
    rangeProblem(a, b) {
      if (Math.round((new Date(b) - new Date(a)) / 86400000) + 1 > 3) {
        return "You can book a maximum of 3 days.";
      }
      for (let d = this.addDays(a, 1); d < b; d = this.addDays(d, 1)) {
        if (this.isUnavailable(d)) return "That range includes a date that is already taken.";
      }
      return "";
    },
    dateEvents(val) {
      return this.isUnavailable(val);
    },
    dateEventColor(val) {
      // Class names (not colours): the dot is restyled in CSS to fill the whole date cell
      if (this.bookedDates.includes(val) || this.heldDates.includes(val)) return "nc-ev-booked";
      return "nc-ev-na";
    },
    viewDrafts() {
      this.draftNotice = false;
      this.pendingCamp = null;
      this.$router.push({ path: "/DraftApplications" });
    },
    sortCampsByDistrict(camps) {
      if (!Array.isArray(camps)) return [];
      const districtList = this.district || [];
      const getDistrictIndex = (dist) => {
        if (!dist) return 999;
        const normalized = String(dist).trim().toLowerCase();
        const idx = districtList.findIndex((d) => {
          const dl = d.toLowerCase();
          return (
            dl === normalized ||
            (dl === "thiruvananthapuram" &&
              (normalized === "trivandrum" || normalized.startsWith("thiruvananthap")))
          );
        });
        return idx === -1 ? 999 : idx;
      };

      return [...camps].sort((a, b) => {
        const idxA = getDistrictIndex(a.district);
        const idxB = getDistrictIndex(b.district);
        if (idxA !== idxB) {
          return idxA - idxB;
        }
        return (a.campName || "")
          .trim()
          .localeCompare((b.campName || "").trim(), undefined, { sensitivity: "base" });
      });
    },
    getList() {
      this.appLoading = true;
      const limit = this.pageLimit;
      this.requestedLimit = limit;
      // Only the latest request may update the list (the limit can change right after mount)
      const seq = (this.listSeq = (this.listSeq || 0) + 1);
      const payload = {
        rangeid: this.pickedDatesid,
        circleid: this.circleid,
        divisionid: this.divisionid,
        status: this.status,
        categoryId: this.categoryId,
        page: this.currentPage,
        fromDate: this.fromDate,
        toDate: this.toDate,
        limit: limit,
        facets: true,
        sort: this.sort,
      };
      if (this.campType !== "all") payload.campType = this.campType;
      if (this.search && this.search.trim()) payload.keyword = this.search.trim();
      if (this.minMembers) payload.minMembers = this.minMembers;

      if (this.districtid) {
        payload.district = this.districtid;
      }

      axios({
        method: "POST",
        url: "/all/naturecamp",
        headers: {
          token: localStorage.getItem("token"),
        },
        data: payload,
      })
        .then((response) => {
          if (seq !== this.listSeq) return;
          if (response.data && response.data.status && Array.isArray(response.data.data)) {
            const rawList = response.data.data;
            this.list =
              this.sort === "recommended" || !this.sort
                ? this.sortCampsByDistrict(rawList)
                : rawList;
            this.totalData = response.data.totalLength !== undefined ? response.data.totalLength : response.data.data.length;
            this.pages = Math.ceil(this.totalData / limit);
            const facets = response.data.facets;
            if (facets) {
              this.typeCounts = facets.typeCounts || {};
              this.divisions = facets.divisions || [];
            }
          } else {
            this.list = [];
            this.totalData = 0;
            this.pages = 0;
          }
          this.appLoading = false;
        })
        .catch((err) => {
          if (seq !== this.listSeq) return;
          this.list = [];
          this.totalData = 0;
          this.pages = 0;
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
  },
};
</script>
  
  <style >
.cus3 {
  height: 50px;
  background-color: white;
}

.vertical-divider {
  border-right: 1px solid black;
  height: 70px;
}

.v-date-picker .v-picker__body {
  box-shadow: none;
}

#chart-container {
  background: linear-gradient(180deg, #29807c 0%, rgba(5, 112, 107, 0.16) 100%);
}

.elevation-3 {
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  transition: 0.3s box-shadow;
}

.elevation-3:hover {
  box-shadow: 0px 8px 12px rgba(0, 0, 0, 0.2);
}

.headline {
  font-size: 18px;
  font-weight: bold;
}

/* Redesigned User Nature Camps UI Elements */
.filter-card {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(10px);
  border-radius: 16px !important;
  border: 1px solid rgba(255, 255, 255, 0.5) !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03) !important;
}

.header-icon-container {
  background: linear-gradient(135deg, #13736f 0%, #29807c 100%);
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 20px rgba(19, 115, 111, 0.25);
}

.page-title {
  font-size: 24px;
  font-weight: 800;
  color: #1a2a29;
  letter-spacing: -0.5px;
  margin-bottom: 2px;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

.modern-select .v-input__control {
  border-radius: 12px !important;
}

.modern-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(19, 115, 111, 0.25) !important;
  border-width: 1px !important;
  border-radius: 12px !important;
}

.modern-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #13736f !important;
  border-width: 2px !important;
}

.camp-card {
  border-radius: 16px !important;
  overflow: hidden !important;
  background: #ffffff !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04) !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
  border: 1px solid rgba(0, 0, 0, 0.03) !important;
}

.camp-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 15px 35px rgba(19, 115, 111, 0.12) !important;
  border-color: rgba(19, 115, 111, 0.2) !important;
}

.camp-card-img {
  border-bottom: 1px solid rgba(0, 0, 0, 0.03);
  transition: transform 0.5s ease;
}

.camp-card:hover .camp-card-img {
  transform: scale(1.03);
}

.camp-district-badge {
  background-color: rgba(19, 115, 111, 0.08);
  color: #13736f;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
}

.camp-members-badge {
  background-color: rgba(0, 0, 0, 0.04);
  color: #64748b;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
}

.camp-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.4;
  height: 50px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: left;
  margin-top: 8px;
}

.apply-btn {
  background: linear-gradient(135deg, #13736f 0%, #29807c 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px;
  border-radius: 10px !important;
  height: 44px !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 15px rgba(19, 115, 111, 0.2) !important;
}

.apply-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(19, 115, 111, 0.3) !important;
}

.camp-type-free-badge {
  background: linear-gradient(135deg, #e6f4ea 0%, #ccead6 100%);
  color: #1b5e20;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  display: inline-block;
  letter-spacing: 0.8px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: default;
  box-shadow: 0 4px 12px rgba(30, 142, 62, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(30, 142, 62, 0.2);
}

.camp-type-paid-badge {
  background: linear-gradient(135deg, #fce8e6 0%, #fad2cf 100%);
  color: #b71c1c;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  display: inline-block;
  letter-spacing: 0.8px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: default;
  box-shadow: 0 4px 12px rgba(217, 48, 37, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(217, 48, 37, 0.2);
}

.camp-type-free-badge:hover {
  background: linear-gradient(135deg, #ccead6 0%, #a8d5b8 100%);
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 8px 20px rgba(30, 142, 62, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(30, 142, 62, 0.4);
}

.camp-type-paid-badge:hover {
  background: linear-gradient(135deg, #fad2cf 0%, #f5b7b1 100%);
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 8px 20px rgba(217, 48, 37, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(217, 48, 37, 0.4);
}

.badge-text {
  display: inline-block;
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.camp-type-free-badge:hover .badge-text,
.camp-type-paid-badge:hover .badge-text {
  transform: scale(1.15);
}

.apply-icon {
  transition: transform 0.2s ease;
}

.apply-btn:hover .apply-icon {
  transform: translateX(4px);
}

.modern-pagination .v-pagination__item {
  border-radius: 8px !important;
  box-shadow: none !important;
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
}

.modern-pagination .v-pagination__navigation {
  border-radius: 8px !important;
  box-shadow: none !important;
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
}
</style>
<style scoped>
/* ---------- Hero ---------- */
.nc-hero {
  position: relative;
  overflow: hidden;
  min-height: 230px;
  margin-bottom: 18px;
  border-radius: 22px;
  background: #0c4f4c;
  box-shadow: 0 16px 40px rgba(12, 79, 76, 0.25);
}

.nc-hero-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.nc-hero-shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(100deg, rgba(8, 51, 49, 0.92) 0%, rgba(12, 79, 76, 0.75) 45%, rgba(12, 79, 76, 0.15) 100%);
}

.nc-hero-content {
  position: relative;
  max-width: 620px;
  padding: 32px 36px;
  color: #ffffff;
}

.nc-hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.nc-hero-title {
  margin: 12px 0 8px;
  font-size: 32px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.6px;
}

.nc-hero-text {
  margin: 0 0 18px;
  max-width: 52ch;
  font-size: 15px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.85);
}

.nc-hero-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.nc-hero-stat {
  padding: 6px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
}

.nc-hero-stat strong {
  color: #fde68a;
  font-size: 15px;
}

/* ---------- Filters ---------- */
.nc-filters {
  margin-bottom: 14px;
  padding: 16px 18px;
  border-radius: 20px;
  background: #ffffff;
  border: 1px solid #e6edf0;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
}

.nc-type-tabs-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.nc-type-tabs {
  display: flex;
  gap: 8px;
}

.nc-how-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border-radius: 999px;
  border: 1.5px solid rgba(19, 115, 111, 0.3);
  background: linear-gradient(135deg, #f0fdf4 0%, #e6f7f6 100%);
  color: #0c4f4c;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 2px 8px rgba(19, 115, 111, 0.06);
}

.nc-how-btn:hover {
  background: #13736f;
  color: #ffffff;
  border-color: #13736f;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(19, 115, 111, 0.25);
}

.nc-how-btn:hover .v-icon {
  color: #ffffff !important;
}

.nc-how-arrow {
  transition: transform 0.2s ease;
}

.nc-how-btn:hover .nc-how-arrow {
  transform: translateX(3px);
}

.nc-type-tab {
  --tab: #13736f;
  --tab-soft: rgba(19, 115, 111, 0.12);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #334155;
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.nc-type-tab--paid {
  --tab: #b45309;
  --tab-soft: rgba(180, 83, 9, 0.14);
}

.nc-type-tab--free {
  --tab: #15803d;
  --tab-soft: rgba(21, 128, 61, 0.13);
}

.nc-type-tab .v-icon {
  color: var(--tab) !important;
}

.nc-type-tab:hover {
  border-color: var(--tab);
}

.nc-type-tab:focus-visible {
  outline: 2px solid var(--tab);
  outline-offset: 2px;
}

.nc-type-tab--active,
.nc-type-tab--active:hover {
  background: var(--tab);
  border-color: var(--tab);
  color: #ffffff;
  box-shadow: 0 6px 14px var(--tab-soft);
}

.nc-type-tab--active .v-icon {
  color: #ffffff !important;
}

.nc-type-count {
  min-width: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: var(--tab-soft);
  color: var(--tab);
  font-size: 12px;
  line-height: 20px;
  text-align: center;
}

.nc-type-tab--active .nc-type-count {
  background: rgba(255, 255, 255, 0.25);
  color: #ffffff;
}

.nc-filter-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.nc-field--search {
  flex: 1 1 auto;
}

.nc-filter-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  margin-top: 10px;
}

.nc-field >>> .v-input__slot {
  border-radius: 12px !important;
}

.nc-field >>> fieldset {
  border-color: #d5e0e3;
}

.nc-field >>> .v-input__prepend-inner .v-icon {
  color: #13736f !important;
}

.v-btn.nc-filter-toggle {
  flex: 0 0 auto;
  height: 40px !important;
  padding: 0 12px !important;
}

.nc-filter-badge {
  margin-left: 6px;
  min-width: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #ffffff;
  color: #13736f;
  font-size: 12px;
  line-height: 20px;
}

/* ---------- Results line ---------- */
.nc-results {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 4px 2px 16px;
  font-size: 14px;
  color: #475569;
}

.nc-results-count strong {
  color: #0f172a;
}

.nc-active-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 3px 4px 3px 10px;
  border-radius: 999px;
  background: #e7f3f2;
  color: #0c4f4c;
  font-size: 12.5px;
  font-weight: 700;
}

.nc-active-chip-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
}

.nc-active-chip-x .v-icon {
  color: #0c4f4c !important;
}

.nc-active-chip-x:hover {
  background: rgba(12, 79, 76, 0.12);
}

.nc-clear {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border: none;
  border-radius: 999px;
  background: rgba(19, 115, 111, 0.1);
  color: #13736f;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
}

@media (max-width: 1100px) {
  .nc-filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* ---------- Cards ---------- */
.nc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  gap: 20px;
}

.nc-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-radius: 20px;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #e6edf0;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.nc-card:hover {
  transform: translateY(-5px);
  border-color: rgba(19, 115, 111, 0.3);
  box-shadow: 0 20px 40px rgba(19, 115, 111, 0.16);
}

.nc-media {
  position: relative;
  height: 190px;
  overflow: hidden;
  background: #e7f1f0;
}

.nc-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.6s ease;
}

.nc-card:hover .nc-img {
  transform: scale(1.06);
}

.nc-media-shade {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(12, 79, 76, 0) 40%, rgba(8, 51, 49, 0.8) 100%);
}

.nc-type {
  position: absolute;
  top: 12px;
  left: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.18);
}

.nc-type--free {
  background: #dcfce7;
  color: #15803d;
}

.nc-type--paid {
  background: #fef3c7;
  color: #b45309;
}

.nc-district {
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  color: #ffffff;
  font-size: 13.5px;
  font-weight: 800;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

.nc-body {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 16px 18px 18px;
}

.nc-title {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.3;
  color: #0f172a;
  text-transform: capitalize;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.nc-caption {
  margin: 0 0 10px;
  font-size: 13.5px;
  line-height: 1.5;
  color: #64748b;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.nc-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 4px 0 16px;
}

.nc-fact {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 5px 10px;
  border-radius: 10px;
  background: #f4f9f8;
  border: 1px solid #e3eeec;
  font-size: 12.5px;
  font-weight: 700;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nc-fact--free {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #15803d;
}

/* v-btn--block sets flex: 1 0 auto, which stretches the button in short cards */
.nc-body .nc-apply {
  flex: 0 0 auto !important;
  margin-top: auto;
}

.nc-apply-icon {
  transition: transform 0.2s ease;
}

.nc-apply:hover .nc-apply-icon {
  transform: translateX(4px);
}

.nc-empty-title {
  font-size: 18px;
  font-weight: 800;
  color: #334155;
}

.nc-pagination {
  display: flex;
  justify-content: center;
  padding: 26px 0 8px;
}

.nc-dialog-text {
  font-size: 15px;
  color: #0f172a !important;
}

.nc-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  justify-content: center;
  margin: 12px 0 8px;
  font-size: 12px;
  color: #475569;
}
.nc-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 5px;
}
.nc-cal-note {
  font-size: 11.5px;
  color: #64748b;
  text-align: center;
}
.nc-cal-summary {
  margin-top: 10px;
  text-align: center;
  font-weight: 700;
  color: #13736f;
}
.nc-dot {
  border: 1.5px solid transparent;
}
/* Booked / held / unavailable dates: the event "dot" is stretched into a filled circle behind the number */
.nc-cal >>> .v-date-picker-table__events {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}
.nc-cal >>> .v-date-picker-table__events > div {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 1.5px solid transparent;
}
.nc-cal >>> .v-date-picker-table__events > .nc-ev-booked {
  background: #fecaca;
  border-color: #dc2626;
}
.nc-cal >>> .v-date-picker-table__events > .nc-ev-na {
  background: #e2e8f0;
  border-color: #94a3b8;
}
.nc-cal >>> .v-date-picker-table .v-btn__content {
  position: relative;
  z-index: 1;
}
/* Today: a solid highlight instead of a plain grey outline */
.nc-cal >>> .v-date-picker-table .v-btn.v-btn--outlined:not(.v-btn--active) {
  background: #13736f !important;
  border: 2px solid #0c4f4c !important;
  box-shadow: 0 2px 6px rgba(19, 115, 111, 0.35);
}
.nc-cal >>> .v-date-picker-table .v-btn.v-btn--outlined:not(.v-btn--active) .v-btn__content {
  color: #fff !important;
  font-weight: 800;
}
.nc-policy {
  display: flex;
  align-items: flex-start;
  margin: 12px 4px 0;
  padding: 10px 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
}
.nc-policy-title {
  font-size: 12.5px;
  font-weight: 700;
  color: #92400e;
}
.nc-policy-text {
  font-size: 11.5px;
  color: #78350f;
  margin-top: 2px;
}
.nc-draft-list {
  margin: 10px 0 4px;
  padding-left: 20px;
  color: #13736f;
  font-weight: 600;
}

.nc-draft-more {
  margin-bottom: 10px;
  font-size: 13px;
  color: #64748b;
}

@media (prefers-reduced-motion: reduce) {
  .nc-card,
  .nc-img,
  .nc-apply-icon {
    transition: none;
  }
}

/* ---------- Phones ---------- */
@media (max-width: 600px) {
  .nc-hero {
    min-height: 0;
    border-radius: 16px;
  }

  .nc-hero-content {
    padding: 22px 20px;
  }

  .nc-hero-title {
    font-size: 24px;
  }

  .nc-hero-text {
    font-size: 14px;
  }

  .nc-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .nc-filters {
    padding: 12px;
    border-radius: 16px;
  }

  .nc-type-tab {
    flex: 1 1 0;
    justify-content: center;
    padding: 8px 10px;
  }

  .nc-how-btn {
    width: 100%;
    justify-content: center;
    margin-top: 4px;
  }

  .nc-filter-grid {
    grid-template-columns: 1fr;
    margin-top: 10px;
  }

  .nc-media {
    height: 170px;
  }
}
</style>
